<div class="account">
  <div class="account__page-header">
    <h1 class="account__page-title">{{ 'account.section.account' | translate }}</h1>
    <p class="account__page-subtitle">{{ 'account.section.accountSubtitle' | translate }}</p>
  </div>

  @if (profile()) {
    <div class="account__profile-card">
      <div class="account__cover">
        <div class="account__avatar" role="img" [attr.aria-label]="profile()!.displayName">{{ initials() }}</div>
      </div>

      <div class="account__profile-body">
        <p class="account__profile-name">{{ profile()!.displayName }}</p>
        <p class="account__profile-email">{{ profile()!.email }}</p>
        <span class="account__role-pill">{{ 'account.role.' + profile()!.role | translate }}</span>
      </div>

      <div class="account__card-divider"></div>

      <dl class="account__metadata">
        <div class="account__metadata-item">
          <dt class="account__metadata-label">{{ 'account.meta.language' | translate }}</dt>
          <dd class="account__metadata-value">{{ currentLanguageLabel() }}</dd>
        </div>
        <div class="account__metadata-item">
          <dt class="account__metadata-label">{{ 'account.meta.timezone' | translate }}</dt>
          <dd class="account__metadata-value">{{ profile()!.timezone ?? browserTimezone }}</dd>
        </div>
        <div class="account__metadata-item">
          <dt class="account__metadata-label">{{ 'account.meta.twoFactor' | translate }}</dt>
          <dd class="account__metadata-value">
            @if (profile()!.twoFactorEnabled === true) {
              {{ 'account.meta.activated' | translate }}
            } @else if (profile()!.twoFactorEnabled === false) {
              {{ 'account.meta.deactivated' | translate }}
            } @else {
              –
            }
          </dd>
        </div>
        <div class="account__metadata-item">
          <dt class="account__metadata-label">{{ 'account.meta.memberSince' | translate }}</dt>
          <dd class="account__metadata-value">
            {{ profile()!.createdAt ? (profile()!.createdAt | date: 'mediumDate') : '–' }}
          </dd>
        </div>
      </dl>
    </div>

    <section class="account__section" [attr.aria-labelledby]="providerTokensHeadingId">
      <to-section-title
        [headingId]="providerTokensHeadingId"
        [title]="'providerTokens.section.title' | translate"
        [subtitle]="'providerTokens.section.subtitle' | translate"
      />
      <app-provider-tokens-section />
    </section>

    <app-ki-mcp-block />

    <section class="account__section" [attr.aria-labelledby]="githubTokenHeadingId">
      <to-section-title
        [headingId]="githubTokenHeadingId"
        [title]="'githubToken.section.title' | translate"
        [subtitle]="'githubToken.section.subtitle' | translate"
      />
      <app-github-token-section [hasToken]="profile()!.hasGithubToken" />
    </section>

    <section class="account__section" [attr.aria-labelledby]="runnerPairingHeadingId">
      <to-section-title
        [headingId]="runnerPairingHeadingId"
        [title]="'runnerPairing.section.title' | translate"
        [subtitle]="'runnerPairing.section.subtitle' | translate"
      />
      <app-runner-pairing-section />
    </section>

    <section class="account__section account__tokens" [attr.aria-labelledby]="tokensHeadingId">
      <to-section-title
        [headingId]="tokensHeadingId"
        [title]="'pat.section.title' | translate"
        [subtitle]="'pat.section.subtitle' | translate"
      >
        <button right mat-flat-button class="account__tokens-create" (click)="openCreateDialog()">
          {{ 'pat.create' | translate }}
        </button>
      </to-section-title>

      @if (tokensLoading()) {
        <div class="account__tokens-loading">
          <mat-progress-spinner mode="indeterminate" diameter="32" />
        </div>
      } @else if (activeTokens().length === 0) {
        <p class="account__tokens-empty">{{ 'pat.empty' | translate }}</p>
      } @else {
        <ul class="account__tokens-list">
          @for (token of activeTokens(); track token.id) {
            <li class="account__token">
              <div class="account__token-main">
                <span class="account__token-name">{{ token.name }}</span>
                <ul class="account__token-scopes">
                  @for (scope of token.scopes; track scope) {
                    <li class="account__token-scope">{{ 'pat.scope.' + scope | translate }}</li>
                  }
                </ul>
              </div>
              <dl class="account__token-meta">
                <div class="account__token-meta-item">
                  <dt>{{ 'pat.meta.expires' | translate }}</dt>
                  <dd>
                    {{ token.expiresAt ? (token.expiresAt | date: 'mediumDate') : ('pat.meta.noExpiry' | translate) }}
                  </dd>
                </div>
                <div class="account__token-meta-item">
                  <dt>{{ 'pat.meta.lastUsed' | translate }}</dt>
                  <dd>
                    {{ token.lastUsedAt ? (token.lastUsedAt | date: 'short') : ('pat.meta.neverUsed' | translate) }}
                  </dd>
                </div>
              </dl>
              <button
                mat-icon-button
                class="account__token-revoke"
                [attr.aria-label]="'pat.revoke' | translate"
                (click)="requestRevoke(token)"
              >
                <mat-icon aria-hidden="true">delete_outline</mat-icon>
              </button>
            </li>
          }
        </ul>
      }
    </section>

    <app-pat-create-dialog
      [visible]="createDialogVisible()"
      [isBusy]="isCreatingToken()"
      [createdToken]="createdToken()"
      (create)="onCreatePat($event)"
      (visibleChange)="onCreateDialogVisibleChange($event)"
    />

    <app-confirm-dialog
      [visible]="revokeTarget() !== null"
      [title]="'pat.revokeDialog.title' | translate"
      [message]="'pat.revokeDialog.message' | translate: { name: revokeTarget()?.name }"
      [confirmLabel]="'pat.revokeDialog.confirm' | translate"
      [cancelLabel]="'pat.revokeDialog.cancel' | translate"
      [busy]="isRevoking()"
      (confirmed)="confirmRevoke()"
      (cancelled)="cancelRevoke()"
    />
  } @else if (profileLoading()) {
    <div class="account__loading">
      <mat-progress-spinner mode="indeterminate" diameter="40" />
    </div>
  }
</div>
